// src/context/SocketContext.jsx
// Socket.io context for real-time updates

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getGuestSessionToken } from '../utils/api';
import { getSocketUrl } from '../config/runtime';

const SocketContext = createContext();

export const useSocket = () => {
	const context = useContext(SocketContext);
	if (!context) {
		throw new Error('useSocket must be used within a SocketProvider');
	}
	return context;
};

export const SocketProvider = ({ children }) => {
	const [socket, setSocket] = useState(null);
	const [isConnected, setIsConnected] = useState(false);
	const [lastMessage, setLastMessage] = useState(null);
	const [authVersion, setAuthVersion] = useState(0);

	useEffect(() => {
		const bumpVersion = () => setAuthVersion((prev) => prev + 1);
		window.addEventListener('eventory:auth-state-changed', bumpVersion);
		return () => window.removeEventListener('eventory:auth-state-changed', bumpVersion);
	}, []);

	// Initialize socket connection
	useEffect(() => {
		// Native build → absolute Heroku origin; web → same-origin / env.
		// See config/runtime.js.
		const serverUrl = getSocketUrl();
		const token = localStorage.getItem('token');
		const guestToken = getGuestSessionToken();

		// No identity yet — e.g. a guest page before `session/start` issues its
		// guest token, or a logged-out marketing page. Opening a socket now just
		// gets rejected with "Authentication required" and burns reconnection
		// attempts, logging connect_error each time. The 'eventory:auth-state-changed'
		// bump re-runs this effect the moment a token (user or guest) is set.
		if (!token && !guestToken) {
			setSocket(null);
			setIsConnected(false);
			return;
		}

		const newSocket = io(serverUrl, {
			transports: ['websocket', 'polling'],
			reconnection: true,
			reconnectionAttempts: 5,
			reconnectionDelay: 1000,
			auth: {
				token: token || undefined,
				guestToken: guestToken || undefined,
			},
		});

		newSocket.on('connect', () => {
			console.log('Socket connected:', newSocket.id);
			setIsConnected(true);
		});

		newSocket.on('disconnect', () => {
			console.log('Socket disconnected');
			setIsConnected(false);
		});

		newSocket.on('connect_error', (error) => {
			console.error('Socket connection error:', error);
			setIsConnected(false);
		});

		setSocket(newSocket);

		return () => {
			newSocket.close();
		};
	}, [authVersion]);

	// Join an event room
	const joinEvent = useCallback((eventId) => {
		if (socket && eventId) {
			socket.emit('joinEvent', eventId);
		}
	}, [socket]);

	// Leave an event room
	const leaveEvent = useCallback((eventId) => {
		if (socket && eventId) {
			socket.emit('leaveEvent', eventId);
		}
	}, [socket]);

	// Join a kitchen room
	const joinKitchen = useCallback((kitchenId) => {
		if (socket && kitchenId) {
			socket.emit('joinKitchen', kitchenId);
		}
	}, [socket]);

	// Leave a kitchen room
	const leaveKitchen = useCallback((kitchenId) => {
		if (socket && kitchenId) {
			socket.emit('leaveKitchen', kitchenId);
		}
	}, [socket]);

	// Join a table room (for customer order tracking)
	const joinTable = useCallback((tableId) => {
		if (socket && tableId) {
			socket.emit('joinTable', tableId);
		}
	}, [socket]);

	// Leave a table room
	const leaveTable = useCallback((tableId) => {
		if (socket && tableId) {
			socket.emit('leaveTable', tableId);
		}
	}, [socket]);

	// Join an order room (for tracking specific order)
	const joinOrder = useCallback((orderId) => {
		if (socket && orderId) {
			socket.emit('joinOrder', orderId);
		}
	}, [socket]);

	// Leave an order room
	const leaveOrder = useCallback((orderId) => {
		if (socket && orderId) {
			socket.emit('leaveOrder', orderId);
		}
	}, [socket]);

	// Subscribe to an event
	const subscribe = useCallback((event, callback) => {
		if (socket) {
			socket.on(event, (data) => {
				setLastMessage({ event, data, timestamp: Date.now() });
				callback(data);
			});
		}
	}, [socket]);

	// Unsubscribe from an event
	const unsubscribe = useCallback((event) => {
		if (socket) {
			socket.off(event);
		}
	}, [socket]);

	// Emit an event
	const emit = useCallback((event, data) => {
		if (socket) {
			socket.emit(event, data);
		}
	}, [socket]);

	const value = {
		socket,
		isConnected,
		lastMessage,
		joinEvent,
		leaveEvent,
		joinKitchen,
		leaveKitchen,
		joinTable,
		leaveTable,
		joinOrder,
		leaveOrder,
		subscribe,
		unsubscribe,
		emit,
	};

	return (
		<SocketContext.Provider value={value}>
			{children}
		</SocketContext.Provider>
	);
};

export default SocketContext;
